import { useGetPipelineDashboardQuery } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import {
  type GetPipelineDashboardRequest,
  type PipelineDashboardItem,
  PipelineName,
  ProcessorJobStatus,
  StatusVariant,
} from '@consigli/types';
import { formatEnumToReadableText } from '@consigli/utils';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { BsThreeDots } from 'react-icons/bs';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { Link } from 'react-router';

import {
  ActionButton,
  Badge,
  DataTable,
  ObjectDetailView,
  Pagination,
  DEFAULT_PAGE_SIZE,
  ProgressBar,
  Select,
  TIME_RANGE_ALL,
  timeRangeOptions,
  type TimeRangeValue,
} from '@/components';
import { PageHeader } from '@/components/page-header';
import { StagesExpandedContent } from '@/pages/services/pipelines/stages-expanded-content';
import { processorJobStatusMapping } from '@/utils/processor-job-status-mapping';

const POLLING_INTERVAL_MS = 15_000;

const ALL_STATUSES = 'ALL_STATUSES';
const ALL_PIPELINES = 'ALL_PIPELINES';
const ALL_JOB_TYPES = 'ALL_JOB_TYPES';
const ALL_PUBLISHED = 'ALL_PUBLISHED';

const publishedOptions = [
  { value: ALL_PUBLISHED, label: 'All' },
  { value: 'true', label: 'Published' },
  { value: 'false', label: 'Unpublished' },
] as const;

type PublishedFilterValue = (typeof publishedOptions)[number]['value'];

const jobTypeOptions = [
  { value: ALL_JOB_TYPES, label: 'All types' },
  { value: 'documentpackage', label: 'Document Package' },
  { value: 'buildingpackage', label: 'Building Package' },
  { value: 'layoutoptimization', label: 'Layout Optimization' },
] as const;

type JobTypeFilterValue = (typeof jobTypeOptions)[number]['value'];

const statusOptions = [ALL_STATUSES, ...Object.values(ProcessorJobStatus)] as const;

const pipelineNameOptions = [ALL_PIPELINES, ...Object.values(PipelineName)] as const;

const getPackageLink = (item: PipelineDashboardItem): string => {
  const base = `/projects/${item.projectId}`;
  switch (item.jobType) {
    case 'documentpackage':
      return `${base}/Packages/${item.packageId}`;
    case 'buildingpackage':
      return `${base}/Buildings/${item.packageId}`;
    case 'layoutoptimization':
      return `${base}/Layouts/${item.packageId}`;
    default:
      return base;
  }
};

export const DashboardPage = () => {
  const [selectedStatus, setSelectedStatus] = useState<string>(ALL_STATUSES);
  const [selectedPipeline, setSelectedPipeline] = useState<string>(ALL_PIPELINES);
  const [selectedJobType, setSelectedJobType] = useState<JobTypeFilterValue>(ALL_JOB_TYPES);
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRangeValue>(TIME_RANGE_ALL);
  const [selectedPublished, setSelectedPublished] = useState<PublishedFilterValue>(ALL_PUBLISHED);
  const [selectedItem, setSelectedItem] = useState<PipelineDashboardItem | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [page, setPage] = useState(1);

  const queryParams = useMemo<GetPipelineDashboardRequest>(() => {
    const params: GetPipelineDashboardRequest = {};

    if (selectedStatus !== ALL_STATUSES) {
      params.status = [selectedStatus as ProcessorJobStatus];
    }
    if (selectedPipeline !== ALL_PIPELINES) {
      params.pipelineName = [selectedPipeline];
    }
    if (selectedJobType !== ALL_JOB_TYPES) {
      params.jobType = selectedJobType;
    }
    if (selectedTimeRange !== TIME_RANGE_ALL) {
      const preset = timeRangeOptions.find((o) => o.value === selectedTimeRange);
      if (preset) {
        const now = new Date();
        const from = new Date(now);
        if (selectedTimeRange === 'TODAY') {
          from.setHours(0, 0, 0, 0);
        } else {
          from.setDate(from.getDate() - preset.days);
        }
        params.createdAfter = from.toISOString();
        params.createdBefore = now.toISOString();
      }
    }

    if (selectedPublished !== ALL_PUBLISHED) {
      params.published = selectedPublished === 'true';
    }

    params.page = page;
    return params;
  }, [
    selectedStatus,
    selectedPipeline,
    selectedJobType,
    selectedTimeRange,
    selectedPublished,
    page,
  ]);

  const { data, isFetching, fulfilledTimeStamp } = useGetPipelineDashboardQuery(queryParams, {
    pollingInterval: POLLING_INTERVAL_MS,
  });

  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  useEffect(() => {
    if (fulfilledTimeStamp) {
      setLastUpdated(
        new Date(fulfilledTimeStamp).toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
      );
    }
  }, [fulfilledTimeStamp]);

  const handleViewDetails = useCallback((item: PipelineDashboardItem) => {
    setSelectedItem(item);
    setIsDetailOpen(true);
  }, []);

  const handleCloseDetail = useCallback((open: boolean) => {
    setIsDetailOpen(open);
    if (!open) setSelectedItem(null);
  }, []);

  const columns: ColumnDef<PipelineDashboardItem>[] = useMemo(
    () => [
      {
        header: 'Pipeline',
        accessorKey: 'pipelineName',
        cell: ({ getValue }) => (
          <span className="font-medium">{formatEnumToReadableText(getValue() as string)}</span>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ getValue }) => {
          const status = getValue() as ProcessorJobStatus;
          const config = processorJobStatusMapping[status];
          return <Badge text={config.text} variant={config.variant} />;
        },
      },
      {
        header: 'Current Stage',
        accessorKey: 'currentStage',
        cell: ({ getValue }) => {
          const stage = getValue() as string | null;
          return stage ? (
            <span>{formatEnumToReadableText(stage)}</span>
          ) : (
            <span className="text-gray-400">—</span>
          );
        },
      },
      {
        header: 'Task Progress',
        cell: ({ row }) => (
          <ProgressBar
            completed={row.original.completedTasks}
            total={row.original.totalTasks}
            failed={row.original.failedTasks}
          />
        ),
      },
      {
        header: 'Project ID',
        accessorKey: 'projectId',
      },
      {
        header: 'Package Name',
        accessorKey: 'packageName',
        cell: ({ row }) => {
          const name = row.original.packageName;
          if (!name) return <span className="text-gray-400">—</span>;
          const truncated = name.length > 60 ? `${name.slice(0, 60)}…` : name;
          return (
            <div className="flex items-center gap-2">
              <span title={name.length > 60 ? name : undefined}>{truncated}</span>
              <Link
                to={getPackageLink(row.original)}
                className="p-1 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-800"
                onClick={(e) => e.stopPropagation()}
              >
                <FaExternalLinkAlt size={12} />
              </Link>
            </div>
          );
        },
      },
      {
        header: 'Job Type',
        accessorKey: 'jobType',
        cell: ({ getValue }) => {
          const jobType = getValue() as string;
          const option = jobTypeOptions.find((o) => o.value === jobType);
          return <span>{option?.label ?? jobType}</span>;
        },
      },
      {
        header: 'Service Type',
        accessorKey: 'serviceType',
        cell: ({ getValue }) => {
          const serviceType = getValue() as string | null;
          return serviceType ? (
            <span>{formatEnumToReadableText(serviceType)}</span>
          ) : (
            <span className="text-gray-400">—</span>
          );
        },
      },
      {
        header: 'Published',
        accessorKey: 'published',
        cell: ({ getValue }) => {
          const published = getValue() as boolean | null;
          if (published === null) return <span className="text-gray-400">—</span>;
          return published ? (
            <Badge text="Published" variant={StatusVariant.SUCCESS} />
          ) : (
            <Badge text="Unpublished" variant={StatusVariant.WARNING} />
          );
        },
      },
      {
        header: '',
        id: 'actions',
        cell: ({ row }) => (
          <ActionButton
            icon={<BsThreeDots size={16} />}
            actions={[
              {
                label: 'Show details',
                onClick: () => handleViewDetails(row.original),
              },
            ]}
          />
        ),
      },
    ],
    [handleViewDetails],
  );

  const items = useMemo(() => data?.results ?? [], [data]);
  const totalCount = data?.count ?? 0;

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="Dashboard" />

      {/* Filters */}
      <div className="flex gap-4 items-end mb-6 flex-wrap">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-500 font-medium">Status</span>
          <Select
            options={[...statusOptions]}
            value={selectedStatus}
            onValueChange={(val) => {
              setSelectedStatus(val);
              setPage(1);
            }}
            getOptionLabel={(opt) =>
              opt === ALL_STATUSES ? 'All statuses' : formatEnumToReadableText(opt)
            }
            getOptionValue={(opt) => opt}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-500 font-medium">Pipeline</span>
          <Select
            options={[...pipelineNameOptions]}
            value={selectedPipeline}
            onValueChange={(val) => {
              setSelectedPipeline(val);
              setPage(1);
            }}
            getOptionLabel={(opt) =>
              opt === ALL_PIPELINES ? 'All pipelines' : formatEnumToReadableText(opt)
            }
            getOptionValue={(opt) => opt}
            showSearch
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-500 font-medium">Time range</span>
          <Select
            options={timeRangeOptions.map((o) => o.value)}
            value={selectedTimeRange}
            onValueChange={(val) => {
              setSelectedTimeRange(val as TimeRangeValue);
              setPage(1);
            }}
            getOptionLabel={(opt) => timeRangeOptions.find((o) => o.value === opt)?.label ?? opt}
            getOptionValue={(opt) => opt}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-500 font-medium">Job type</span>
          <Select
            options={jobTypeOptions.map((o) => o.value)}
            value={selectedJobType}
            onValueChange={(val) => {
              setSelectedJobType(val as JobTypeFilterValue);
              setPage(1);
            }}
            getOptionLabel={(opt) => jobTypeOptions.find((o) => o.value === opt)?.label ?? opt}
            getOptionValue={(opt) => opt}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-500 font-medium">Published</span>
          <Select
            options={publishedOptions.map((o) => o.value)}
            value={selectedPublished}
            onValueChange={(val) => {
              setSelectedPublished(val as PublishedFilterValue);
              setPage(1);
            }}
            getOptionLabel={(opt) => publishedOptions.find((o) => o.value === opt)?.label ?? opt}
            getOptionValue={(opt) => opt}
          />
        </div>
        {(selectedStatus !== ALL_STATUSES ||
          selectedPipeline !== ALL_PIPELINES ||
          selectedJobType !== ALL_JOB_TYPES ||
          selectedTimeRange !== TIME_RANGE_ALL ||
          selectedPublished !== ALL_PUBLISHED) && (
          <Button
            variant="link"
            size="sm"
            className="self-end"
            onClick={() => {
              setSelectedStatus(ALL_STATUSES);
              setSelectedPipeline(ALL_PIPELINES);
              setSelectedJobType(ALL_JOB_TYPES);
              setSelectedTimeRange(TIME_RANGE_ALL);
              setSelectedPublished(ALL_PUBLISHED);
              setPage(1);
            }}
          >
            Clear filters
          </Button>
        )}
        <div className="ml-auto self-end text-xs text-gray-400">
          {lastUpdated && <span>Last updated {lastUpdated}</span>}
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={items}
        isLoading={isFetching}
        scrollable={true}
        height="70vh"
        expandOnRowClick
        expandedContent={(item) => (
          <StagesExpandedContent projectId={item.projectId} jobId={item.id} />
        )}
      />
      <div className="flex justify-center my-4 flex-shrink-0">
        <Pagination
          currentPage={page}
          totalCount={totalCount}
          pageSize={DEFAULT_PAGE_SIZE}
          onPageChange={setPage}
        />
      </div>

      {selectedItem && (
        <ObjectDetailView
          object={selectedItem}
          objectType="Pipeline Job"
          summary={`Pipeline "${selectedItem.pipelineName}" — ${selectedItem.completedTasks}/${selectedItem.totalTasks} tasks completed`}
          isOpen={isDetailOpen}
          onOpenChange={handleCloseDetail}
        />
      )}
    </div>
  );
};
